import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';

function SignupPage() {
  const { signup, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Onboarding state — after successful signup
  const [apiKey, setApiKey] = useState(null);
  const [copied, setCopied] = useState(false);

  if (isAuthenticated && !apiKey) {
    navigate('/dashboard');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      await signup(email, password, name);
      // Auto-create their first API key
      try {
        const keyRes = await api.billing.createKey('Default');
        setApiKey(keyRes.data.key);
      } catch {
        // Key creation failed — still redirect to dashboard
        navigate('/dashboard');
      }
    } catch (err) {
      const data = err.response?.data;
      if (data) {
        const msg = data.email?.[0] || data.password?.[0] || data.error || data.detail || 'Registration failed.';
        setError(msg);
      } else {
        setError('Registration failed. Please try again.');
      }
    }
    setLoading(false);
  };

  const copyKey = () => {
    navigator.clipboard.writeText(apiKey).then(() => setCopied(true));
    setTimeout(() => setCopied(false), 2000);
  };

  // ─── Onboarding screen: show the key ───
  if (apiKey) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <Helmet>
          <title>Welcome to CommunityGuardAPI</title>
        </Helmet>
        <div className="w-full max-w-lg">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-8 shadow-xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-white mb-1">You're in!</h1>
              <p className="text-slate-400 text-sm">Your account is ready. Here's your API key.</p>
            </div>

            {/* API Key display */}
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Your API Key</span>
                <span className="text-[10px] text-red-400 font-medium">Shown once only</span>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-slate-900 text-green-400 px-3 py-2.5 rounded-lg text-xs sm:text-sm font-mono break-all select-all">
                  {apiKey}
                </code>
                <button
                  onClick={copyKey}
                  className={`shrink-0 px-4 py-2.5 rounded-lg text-sm font-semibold transition ${
                    copied
                      ? 'bg-green-500 text-white'
                      : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                  }`}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Quick start */}
            <div className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden mb-6">
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800/50 border-b border-slate-700">
                <span className="text-xs font-mono text-slate-500">Try it now</span>
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-red-500/60"></span>
                  <span className="w-2 h-2 rounded-full bg-yellow-500/60"></span>
                  <span className="w-2 h-2 rounded-full bg-green-500/60"></span>
                </div>
              </div>
              <pre className="p-4 text-xs overflow-x-auto">
                <code className="text-slate-300">
{`curl -H "X-API-Key: ${apiKey.slice(0, 20)}..." \\
  "https://communityguardapi.com/api/data/search/\\
?lastName=Smith&state=FL&page_size=5"`}
                </code>
              </pre>
            </div>

            {/* What you get */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { num: '100', label: 'Free calls/mo' },
                { num: '50', label: 'States covered' },
                { num: '580K+', label: 'Records' },
              ].map(({ num, label }) => (
                <div key={label} className="text-center bg-slate-700/30 rounded-lg p-3">
                  <div className="text-lg font-bold text-emerald-400">{num}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-2">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg shadow-lg transition"
              >
                Go to Dashboard
              </button>
              <Link
                to="/api-docs"
                className="w-full py-3 border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 rounded-lg font-medium transition text-center text-sm"
              >
                Read the Docs
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Signup form ───
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <Helmet>
        <title>Create Free Account — CommunityGuardAPI</title>
        <link rel="canonical" href="https://communityguardapi.com/signup" />
        <meta name="description" content="Sign up for free access to the sex offender registry API. 100 free API calls per month. No credit card required." />
        <meta property="og:title" content="Create Free Account — CommunityGuardAPI" />
        <meta property="og:description" content="Sign up for free access to the sex offender registry API. 100 free API calls per month. No credit card required." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://communityguardapi.com/signup" />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      <div className="w-full max-w-md">
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-8 shadow-xl">
          <h1 className="text-3xl font-bold text-white mb-2 text-center">Create Account</h1>
          <p className="text-slate-400 text-center mb-8">Get your API key instantly — 100 free calls/month</p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 mb-6 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Full Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={8}
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
                placeholder="Min. 8 characters"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
                placeholder="Confirm your password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-emerald-500 hover:bg-emerald-500/80 disabled:opacity-50 text-white font-semibold rounded-lg shadow-lg transition duration-200"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                  Creating account...
                </span>
              ) : 'Create Account & Get API Key'}
            </button>

            <p className="text-xs text-slate-500 text-center">
              No credit card required. By signing up, you agree to our <Link to="/terms" className="text-emerald-400 hover:text-amber-400 transition">Terms of Service</Link>.
            </p>
          </form>

          <p className="mt-6 text-center text-slate-400 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-emerald-400 hover:text-amber-400 font-medium transition">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignupPage;
